import React from 'react';

import { cn } from '@/lib/utils';

import { gradientDecodoBorderStyling } from '@/styles/gradient';

import { passDataTrackerRef } from '../Button/utils';

export const sizesOptions = ['s', 'm', 'l'] as const;
const sizes: Record<(typeof sizesOptions)[number], string> = {
  s: 'px-2 py-[2px] text-label-s-medium',
  m: 'px-2 py-1 text-label-s-medium',
  l: 'px-2 py-1 text-label-medium',
};

const gradientExtraSizes: Record<(typeof sizesOptions)[number], string> = {
  s: 'px-[9px] py-[3px]',
  m: 'px-[9px] pt-[4px] pb-[3px]',
  l: 'px-[9px] py-[5px]',
};

export const colorOptionValues = [
  'gray',
  'success',
  'tertiary',
  'brand',
  'secondary',
  'error',
  'warning',
  'gradient',
] as const;
export type ColorOption = (typeof colorOptionValues)[number];

export const variantOptions = ['filled', 'outlined', 'soft'] as const;
export type Variant = (typeof variantOptions)[number];

const colorClassesMap: Record<ColorOption, Record<Variant, string>> = {
  gray: {
    filled: 'bg-gray-900 text-base-white',
    outlined: 'bg-transparent border-gray-400 text-gray-900',
    soft: 'bg-gray-200 text-base-black',
  },
  success: {
    filled: 'bg-success-700 text-base-white dark:bg-success-300',
    outlined:
      'bg-transparent border-success-700 text-success-700 dark:border-success-300 dark:text-success-300',
    soft: 'bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300',
  },
  tertiary: {
    filled: 'bg-tertiary-700 text-base-white dark:bg-tertiary-300',
    outlined:
      'bg-transparent border-tertiary-700 text-tertiary-700 dark:border-tertiary-300 dark:text-tertiary-300',
    soft: 'bg-tertiary-100 text-tertiary-700 dark:bg-tertiary-900 dark:text-tertiary-300',
  },
  brand: {
    filled: 'bg-brand-500 text-base-white',
    outlined:
      'bg-transparent border-brand-500 text-brand-600 dark:text-brand-400',
    soft: 'bg-brand-100 text-brand-700 dark:bg-brand-900 dark:text-brand-300',
  },
  secondary: {
    filled: 'bg-secondary-700 text-base-white dark:bg-secondary-300',
    outlined:
      'bg-transparent border-secondary-700 text-secondary-700 dark:border-secondary-300 dark:text-secondary-300',
    soft: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-900 dark:text-secondary-300',
  },
  error: {
    filled: 'bg-error-600 text-base-white dark:bg-error-400',
    outlined:
      'bg-transparent border-error-600 text-error-600 dark:border-error-400 dark:text-error-400',
    soft: 'bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-300',
  },
  warning: {
    filled: 'bg-warning-700 text-base-white dark:bg-warning-300',
    outlined:
      'bg-transparent border-warning-700 text-warning-700 dark:border-warning-300 dark:text-warning-300',
    soft: 'bg-warning-200 text-warning-700 dark:bg-warning-800 dark:text-warning-300',
  },
  gradient: {
    filled: 'text-base-black bg-gradient-main dark:text-base-dark-white',
    outlined: 'text-base-black dark:text-base-dark-white',
    soft: 'text-base-dark-white bg-gradient-secondary-1',
  },
};

export interface BadgeProps {
  variant?: Variant;
  size?: keyof typeof sizes;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  color?: ColorOption;
  classNames?: {
    root?: string;
  };
}

interface BadgePropsWithHtml
  extends BadgeProps,
    Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  children: React.ReactNode;
}

export default function Badge({
  children,
  classNames,
  color = 'gradient',
  variant = 'outlined',
  size = 's',
  leftSection,
  rightSection,
  ...rest
}: BadgePropsWithHtml) {
  const isGradient = color === 'gradient';
  const isGradientFill = isGradient && variant !== 'outlined';
  const isGradientBorder = isGradient && variant === 'outlined';
  const isNonGradientOutline = variant === 'outlined' && !isGradient;

  const colorClasses =
    colorClassesMap[color]?.[variant] ?? colorClassesMap.success.filled;

  const borderClasses = isGradientBorder
    ? `${gradientDecodoBorderStyling({
        borderWidth: 'p-px',
        color: 'gradient-main',
        borderRadius: 'rounded-3xl',
      })} ${gradientExtraSizes[size]} relative !rounded-3xl`
    : 'rounded-3xl';

  return (
    <div
      {...passDataTrackerRef<HTMLDivElement>()}
      className={`${cn(
        'flex w-fit items-center justify-center gap-1.5 whitespace-nowrap align-middle font-serif uppercase',
        rest.onClick ? 'cursor-pointer' : 'cursor-default',
        colorClasses,
        sizes[size],
        isGradientFill && gradientExtraSizes[size],
        borderClasses,
        isNonGradientOutline && 'border border-solid',
        classNames?.root
      )}`}
      data-tracker-component='badge'
      {...rest}
    >
      {leftSection}
      {children}
      {rightSection}
    </div>
  );
}
